
import Vue, { PropType } from 'vue'
import api from '@root/src/api/api'
import SpinnerLoader from '@root/src/components/SpinnerLoader.vue'
import Opz2Metrics from './Opz2Metrics.vue'
import Opz2Empty from './Opz2Empty.vue'
import { numSort, floatNumSort } from '@root/src/utils'
import {
    EMPTY_DICTIONARIES,
    Opz2Dictionaries,
    crmAdminLabel,
    clientName,
    formatDate,
    formatNumber,
    formatPercent,
    share,
    staffName,
    statusLabel,
    statusTag,
} from './opz2'

export default Vue.extend({
    name: 'Opz2MastersTab',
    components: { SpinnerLoader, Opz2Metrics, Opz2Empty },
    props: {
        projectId: { type: String, required: true },
        filters: { type: Object as PropType<any>, required: true },
        active: { type: Boolean, default: false },
    },
    data: () => ({
        loading: false,
        error: false,
        requestId: 0,
        unclassifiedEvents: 0,
        reportData: null as any,
        dictionaries: EMPTY_DICTIONARIES as Opz2Dictionaries,
        footerProps: { 'items-per-page-options': [10, 25, 50] },
    }),
    computed: {
        /** Итоги по всем ОПЗ на то же направление. */
        total(): any {
            return this.reportData?.statByStaffTotal ?? {}
        },
        hasData(): boolean {
            return Number(this.total.recordedSameSpecCnt ?? 0) > 0
        },
        metrics(): any[] {
            const all = Number(this.total.recordedSameSpecCnt ?? 0)
            const other = Number(this.total.recordedSameSpecOtherStaffCnt ?? 0)
            const success = Number(this.total.recordedSameSpecSuccessCnt ?? 0)
            return [
                {
                    label: 'ОПЗ на то же направление',
                    value: formatNumber(all),
                    hint: '(тот же тип услуг)',
                },
                {
                    label: 'ОПЗ к другому мастеру',
                    value: formatNumber(other),
                    hint: 'не кросс-продажи',
                    tone: 'wasabi',
                },
                {
                    label: 'Завершённые',
                    value: formatNumber(success),
                    hint: `${formatPercent(share(success, all))} от ОПЗ 2.0`,
                    tone: 'teal',
                },
                {
                    label: 'Отменено',
                    value: formatNumber(this.total.recordedSameSpecCancelCnt),
                    hint: `${formatPercent(this.total.recordedSameSpecCancelPercent)} отмен за период`,
                    tone: 'red',
                },
            ]
        },
        staffHeaders(): any[] {
            // ширины колонок — из макета v16 (colgroup сводной таблицы)
            return [
                { text: 'Мастер', value: 'staff', align: 'left', width: '14%' },
                { text: 'Визитов', value: 'visits', sort: numSort, align: 'center', width: '7%' },
                {
                    text: 'Всего ОПЗ (тот же тип услуг)',
                    value: 'opzAll',
                    sort: numSort,
                    align: 'center',
                    width: '10%',
                },
                {
                    text: 'ОПЗ к этому мастеру',
                    value: 'opzOwn',
                    sort: numSort,
                    align: 'center',
                    width: '10%',
                },
                {
                    text: 'ОПЗ к другому мастеру',
                    value: 'opzOther',
                    sort: numSort,
                    align: 'center',
                    width: '10%',
                },
                {
                    text: 'Конверсия визитов на ОПЗ',
                    value: 'conv',
                    sort: floatNumSort,
                    align: 'center',
                    width: '12%',
                },
                {
                    text: 'Записи на будущее',
                    value: 'future',
                    sort: numSort,
                    align: 'center',
                    width: '9%',
                },
                {
                    text: 'Завершённые',
                    value: 'success',
                    sort: numSort,
                    align: 'center',
                    width: '10%',
                },
                { text: 'Отменено', value: 'cancel', sort: numSort, align: 'center', width: '8%' },
                {
                    text: '% отмен за период',
                    value: 'cancelPercent',
                    sort: floatNumSort,
                    align: 'center',
                    width: '10%',
                },
            ]
        },
        /** Средняя конверсия за период — порог подсветки лидеров (tr.hot в макете). */
        avgConv(): number {
            return Number(this.total.recordedSameSpecOpzConv ?? 0)
        },
        staffRows(): any[] {
            return (this.reportData?.statByStaff ?? []).map((row: any) => {
                const conv = Number(row.recordedSameSpecOpzConv ?? 0)
                const isHot = this.avgConv > 0 && conv > this.avgConv
                return {
                    staff: staffName(this.dictionaries, row.staffId),
                    visits: formatNumber(row.visitsCnt),
                    opzAll: formatNumber(row.recordedSameSpecCnt),
                    opzOwn: formatNumber(row.recordedSameSpecAndStaffCnt),
                    opzOther: formatNumber(row.recordedSameSpecOtherStaffCnt),
                    conv: formatPercent(conv),
                    future: formatNumber(row.recordedSameSpecFutureCnt),
                    success: formatNumber(row.recordedSameSpecSuccessCnt),
                    cancel: formatNumber(row.recordedSameSpecCancelCnt),
                    cancelPercent: formatPercent(row.recordedSameSpecCancelPercent),
                    isHot,
                    pctTone: isHot ? 'hi' : conv < this.avgConv / 2 ? 'lo' : 'base',
                }
            })
        },
        staffTotalRow(): any {
            if (!this.reportData?.statByStaff?.length) return null
            return {
                staff: 'Итого',
                visits: formatNumber(this.total.visitsCnt),
                opzAll: formatNumber(this.total.recordedSameSpecCnt),
                opzOwn: formatNumber(this.total.recordedSameSpecAndStaffCnt),
                opzOther: formatNumber(this.total.recordedSameSpecOtherStaffCnt),
                conv: formatPercent(this.total.recordedSameSpecOpzConv),
                future: formatNumber(this.total.recordedSameSpecFutureCnt),
                success: formatNumber(this.total.recordedSameSpecSuccessCnt),
                cancel: formatNumber(this.total.recordedSameSpecCancelCnt),
                cancelPercent: formatPercent(this.total.recordedSameSpecCancelPercent),
            }
        },
        detailHeaders(): any[] {
            return [
                { text: 'Клиент', value: 'client', align: 'left', width: '22%' },
                { text: 'Мастер', value: 'staff', align: 'left', width: '22%' },
                { text: 'Дата записи', value: 'visitDate', align: 'center', width: '14%' },
                { text: 'Визит по ОПЗ', value: 'opzVisitDate', align: 'center', width: '14%' },
                { text: 'Статус', value: 'status', align: 'center', width: '14%' },
                { text: 'Аккаунт', value: 'account', align: 'left' },
            ]
        },
        detailRows(): any[] {
            return (this.reportData?.sameSpecDetails ?? []).map((row: any) => ({
                client: clientName(this.dictionaries, row.clientId),
                staff: staffName(this.dictionaries, row.staffId),
                visitDate: formatDate(row.visitDate),
                opzVisitDate: formatDate(row.opzVisitDate),
                status: statusLabel(row.opzStatus),
                statusTag: statusTag(row.opzStatus),
                account: crmAdminLabel(this.dictionaries, row.opzCreatingUserId),
            }))
        },
    },
    watch: {
        active: {
            immediate: true,
            handler() {
                this.loadIfNeeded()
            },
        },
        filters() {
            this.loadIfNeeded()
        },
    },
    beforeDestroy() {
        this.requestId++
    },
    methods: {
        staffRowClass(row: any): string {
            return row.isHot ? 'opz2-row--hot' : ''
        },
        loadIfNeeded() {
            if (!this.active) return
            this.load()
        },
        async load() {
            const requestId = ++this.requestId
            this.loading = true
            this.error = false
            this.$emit('loading', true)
            try {
                const { data, error } = await api.gcb2.getOpz2Data({
                    projectId: this.projectId,
                    filters: this.filters,
                    tabs: ['masters'],
                })
                if (requestId !== this.requestId) return
                if (error || !data) {
                    this.error = true
                    return
                }
                this.reportData = data.reportData
                this.unclassifiedEvents = Number(data.meta?.unclassifiedEvents ?? 0)
                this.dictionaries = data.dictionaries ?? EMPTY_DICTIONARIES
            } catch (err) {
                if (requestId === this.requestId) this.error = true
            } finally {
                if (requestId === this.requestId) {
                    this.loading = false
                    this.$emit('loading', false)
                }
            }
        },
    },
})
